import type {
  BriqpayOrderStatus,
  BriqpaySession,
  IngridCompleteSessionResponse,
  Option,
  OrderDTO,
  PaymentProviderConfig,
  ShippingProviderConfig,
  ShippingProviderName
} from 'ecosystem'
import { OrderStatus } from 'ecosystem'
import { getStorefrontOrderByOrderNumber } from '../getStorefrontOrderByOrderNumber'
import { updateStorefrontOrderStatus } from '../updateStorefrontOrderStatus'
import { briqpayApi } from '../../../utils/briqpay'
import { createError } from '../../../utils/fetching'
import { alertService } from '../../../utils/alert-service'
import { completeStorefrontOrder } from './completeStorfrontOrder'
import type { OrderProvisionResult, ProvisionOrderOptions } from './types'
import {
  generateBriqpayAddresses,
  generateIngridShippingProviderResult,
  generateNshiftShippingProviderResult,
  generatePostnordShippingProviderResult,
  isOrderAlreadyProvisioned,
  isOrderFresh
} from './utils'

export interface ProvisionOrderBriqpayProviderResult {
  briqpayResponse: BriqpaySession
  storefrontOrder: Option<OrderDTO>
  shippingProviderResult?: {
    providerName: ShippingProviderName
    ingridResponse?: IngridCompleteSessionResponse['session']
  }
}

/**
 * Order states we are willing to provision on.
 *
 * Briqpay reports payment progress separately from the session: a session flips to
 * `completed` the moment the shopper finishes, but the order sits at `order_pending`
 * until the PSP answers, which Briqpay documents as "usually within a few minutes".
 *
 * We provision on `order_pending` so the confirmation page has an order to render and the
 * analytics purchase event fires, and let a later `order_rejected` webhook flip the order
 * to CANCELLED. Waiting for `order_approved_not_captured` instead would leave the shopper
 * looking at a missing order for minutes. Enabling `realTimeProcessing` on the store
 * shortens that window.
 */
const PROVISIONABLE_ORDER_STATUSES: BriqpayOrderStatus[] = [
  'order_pending',
  'order_approved_not_captured',
  'captured_partial',
  'captured_full'
]

/**
 * Terminal states that mean the payment will never happen. Because we provision on
 * `order_pending` (above), a rejection can land against an order we already flipped to PAID,
 * so it has to be walked back to CANCELLED. `order_cancel_pending` is deliberately absent —
 * it is not terminal yet.
 */
const CANCELLED_ORDER_STATUSES: BriqpayOrderStatus[] = ['order_rejected', 'order_cancelled']

export const provisionOrderBriqpayProvider = async (
  storefrontOrderNumber: string,
  paymentProviderConfig?: PaymentProviderConfig,
  shippingProviderConfig?: ShippingProviderConfig,
  options?: ProvisionOrderOptions
): Promise<Option<ProvisionOrderBriqpayProviderResult>> => {
  // 1. Get storefront order. Briqpay's `urls.redirect` and our webhook URLs both carry
  //    the SF order number rather than the sessionId — there is no template support — so
  //    the identifier is always ours, whichever path called us.
  const { error: getOrderError, data: orderData } = await getStorefrontOrderByOrderNumber({
    orderNumber: storefrontOrderNumber
  })

  if (getOrderError || !orderData) {
    void alertService.send('Provision order | Get storefront order', {
      blocks: [
        { title: 'Payload', content: { storefrontOrderNumber } },
        { title: 'Error', content: getOrderError }
      ]
    })

    throw createError(getOrderError ?? 'BE GET endpoint error')
  }

  // 2. Recover the Briqpay sessionId, persisted at create time by
  //    createOrderHandler/briqpayPaymentProvider.ts.
  if (!orderData.paymentId) {
    void alertService.send('Provision order | SF order missing Briqpay paymentId', {
      blocks: [
        { title: 'Payload', content: { storefrontOrderNumber } },
        { title: 'Storefront Order', content: orderData }
      ]
    })

    throw createError('SF order is missing Briqpay paymentId — cannot resolve Briqpay session')
  }

  // 3. Read the session. The hook body is never trusted for amounts or status — this
  //    read is the only source of truth.
  const { error: briqpayError, data: briqpaySession } = await briqpayApi.checkout.readSession(
    orderData.paymentId
  )

  if (briqpayError || !briqpaySession) {
    void alertService.send('Provision order | Get briqpay session', {
      blocks: [
        { title: 'Payload', content: { sessionId: orderData.paymentId } },
        { title: 'Error', content: briqpayError }
      ]
    })

    throw Error(briqpayError || 'Briqpay GET endpoint error')
  }

  const orderStatus = briqpaySession.moduleStatus?.payment?.orderStatus

  // 4. Terminal rejection. Handled here rather than in the push endpoint because this read —
  //    not the webhook body — is the only thing we trust about payment status, and because a
  //    rejection must land whether the order is still CREATED or already PAID (so it runs
  //    ahead of both guards below).
  if (orderStatus && CANCELLED_ORDER_STATUSES.includes(orderStatus)) {
    if (orderData.status !== OrderStatus.CANCELLED) {
      const { error: cancelError } = await updateStorefrontOrderStatus({
        orderId: storefrontOrderNumber,
        payload: { orderStatus: OrderStatus.CANCELLED }
      })

      if (cancelError) {
        void alertService.send('Provision order | Cancel rejected Briqpay order', {
          blocks: [
            { title: 'Payload', content: { storefrontOrderNumber, orderStatus } },
            { title: 'Error', content: cancelError }
          ]
        })

        // Leaving an order PAID for a rejected payment is the one outcome worth failing
        // loudly over: throwing makes the push endpoint answer non-2xx so Briqpay retries.
        throw createError(cancelError)
      }
    }

    // The payment push just needs the cancellation acknowledged. The confirmation page, which
    // never passes `asFallback`, has a shopper in front of it and must render the failure
    // instead of a receipt — so it gets a throw.
    if (options?.asFallback) {
      return {
        storefrontOrder: { ...orderData, status: OrderStatus.CANCELLED },
        briqpayResponse: briqpaySession
      }
    }

    throw createError(`Briqpay payment did not go through (orderStatus: ${orderStatus})`)
  }

  // 5. Idempotency guard: the order is provisioned exactly once. If it is no longer CREATED,
  //    another path (confirmation page, or an earlier push retry) already provisioned it —
  //    return the existing order without re-booking shipment or re-writing it. This sits above
  //    the payability checks so a late hook on a settled order (a capture, say) is acknowledged
  //    rather than retried forever.
  if (isOrderAlreadyProvisioned(orderData)) {
    return { storefrontOrder: orderData, briqpayResponse: briqpaySession }
  }

  // 6. Validate both stages. `status: 'completed'` alone is not payment success.
  if (briqpaySession.status !== 'completed') {
    void alertService.send('Provision order | Briqpay session not completed', {
      blocks: [
        { title: 'Payload', content: { sessionId: briqpaySession.sessionId } },
        {
          title: 'Error',
          content: `Briqpay session status is ${briqpaySession.status}, expected 'completed'`
        }
      ]
    })

    throw Error('Briqpay session is not completed yet')
  }

  if (!orderStatus || !PROVISIONABLE_ORDER_STATUSES.includes(orderStatus)) {
    void alertService.send('Provision order | Briqpay order not payable', {
      blocks: [
        { title: 'Payload', content: { sessionId: briqpaySession.sessionId } },
        { title: 'Briqpay Session', content: briqpaySession },
        {
          title: 'Error',
          content: `Briqpay orderStatus is ${orderStatus ?? 'missing'}, expected one of ${PROVISIONABLE_ORDER_STATUSES.join(', ')}`
        }
      ]
    })

    throw Error(`Briqpay order is not payable (orderStatus: ${orderStatus ?? 'missing'})`)
  }

  // 7. Fallback gate (payment push only): a still-fresh order belongs to the confirmation
  // page; throw so the push endpoint responds non-2xx and the provider retries later.
  if (options?.asFallback && isOrderFresh(orderData)) {
    throw createError(
      'Payment push received for a still-fresh order; deferring to the confirmation page'
    )
  }

  const getShippingProviderResult = async (): Promise<
    OrderProvisionResult['shippingProviderResult'] | void
  > => {
    if (!orderData.shippingProvider) {
      return
    }

    switch (orderData.shippingProvider) {
      case 'INGRID':
        return generateIngridShippingProviderResult(orderData)
      case 'POSTNORD': {
        const { customerInfo } = generateBriqpayAddresses(briqpaySession, orderData)

        return generatePostnordShippingProviderResult(
          orderData,
          {
            phoneNo: customerInfo.phone,
            smsNo: customerInfo.phone,
            emailAddress: customerInfo.email,
            contactName: customerInfo.fullName
          },
          shippingProviderConfig
        )
      }
      case 'NSHIFT': {
        const { customerInfo } = generateBriqpayAddresses(briqpaySession, orderData)

        return generateNshiftShippingProviderResult(orderData, {
          phone: customerInfo.phone,
          email: customerInfo.email,
          name: customerInfo.fullName
        })
      }
      case 'DEFAULT':
    }
  }

  // 8. Get shipping provider information
  const shippingProviderResult = await getShippingProviderResult()

  // 9. Complete storefront order, change status to PAID and update all info
  const storefrontOrder = await completeStorefrontOrder({
    order: orderData,
    paymentProviderInfo: {
      ...paymentProviderConfig,
      providerName: 'BRIQPAY',
      providerResponse: {
        briqpayResponse: briqpaySession
      }
    },
    ...(shippingProviderResult && {
      shippingProviderInfo: shippingProviderResult
    })
  })

  return {
    storefrontOrder,
    briqpayResponse: briqpaySession,
    ...(shippingProviderResult && {
      shippingProviderResult
    })
  }
}
